/** Academy back office: defensive readers for the JSON blocks of the API (format, analysis, progress). */

import { ApiError } from "@agencyos/api-client";

import { TIME_ZONE } from "../billing/shared";
import { isObject, num, str } from "./shared";

export const EXAM_SKILLS = ["co", "ce", "ee", "eo"] as const;
export type ExamSkill = (typeof EXAM_SKILLS)[number];

// --- Centres and exam structure (format table, versioned in the database) --------------------

export interface ExamCenter {
  code: string;
  city: string;
  name: string;
}

/** `academy/format` → centers [{code, city, name}] (Alliance française centres in Cameroon). */
export function centersOf(value: unknown): ExamCenter[] {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).filter(isObject).flatMap((c) => {
    const city = str(c.city);
    return city ? [{ code: str(c.code) || city, city, name: str(c.name) || city }] : [];
  });
}

/** Official structure read from the format table: CO/CE questions, EE/EO tasks per section. */
export function expectedStructure(sections: unknown): Record<ExamSkill, number> | null {
  if (!isObject(sections)) return null;
  const out: Partial<Record<ExamSkill, number>> = {};
  for (const skill of EXAM_SKILLS) {
    const section = sections[skill];
    if (!isObject(section)) return null;
    out[skill] = skill === "co" || skill === "ce" ? num(section.questions) : isObject(section.tasks) ? Object.keys(section.tasks).length : 0;
  }
  return out as Record<ExamSkill, number>;
}

export interface StructureCheck {
  skill: ExamSkill;
  count: number;
  expected: number;
  ok: boolean;
}

/** Counts of a mock exam against the official structure (CO 39, CE 39, EE 3, EO 3 today). */
export function structureCheck(counts: Record<string, number> | undefined, expected: Record<ExamSkill, number>): { rows: StructureCheck[]; ok: boolean } {
  const rows = EXAM_SKILLS.map((skill) => {
    const count = num(counts?.[skill]);
    return { skill, count, expected: expected[skill], ok: count === expected[skill] };
  });
  return { rows, ok: rows.every((r) => r.ok) };
}

// --- Month analysis --------------------------------------------------------------------------

export interface ThemeCount {
  theme: string;
  count: number;
}

export interface MonthAnalysisData {
  month: string;
  center: string;
  reports: number;
  centers: { center: string; count: number }[];
  byCenter: { center: string; themes: ThemeCount[] }[];
  themes: { ee: ThemeCount[]; eo: ThemeCount[] };
  difficulty: number | null;
  officialNclc: { skill: string; value: number }[];
}

function themeList(value: unknown): ThemeCount[] {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).filter(isObject).flatMap((row) => {
    const theme = str(row.theme);
    return theme ? [{ theme, count: num(row.count) }] : [];
  });
}

/** `monthly-packs/analysis` → { analysis: {...} } (or the analysis itself). */
export function parseAnalysis(value: unknown): MonthAnalysisData {
  const data = isObject(value) && isObject(value.analysis) ? value.analysis : isObject(value) ? value : {};
  const centers = isObject(data.centers) ? data.centers : {};
  const byCenter = isObject(data.by_center) ? data.by_center : {};
  const themes = isObject(data.themes) ? data.themes : {};
  const nclc = isObject(data.official_nclc) ? data.official_nclc : {};
  return {
    month: str(data.month),
    center: str(data.center),
    reports: num(data.reports),
    centers: Object.entries(centers)
      .map(([center, count]) => ({ center, count: num(count) }))
      .sort((a, b) => b.count - a.count || a.center.localeCompare(b.center)),
    byCenter: Object.entries(byCenter)
      .map(([center, rows]) => ({ center, themes: themeList(rows) }))
      .sort((a, b) => a.center.localeCompare(b.center)),
    themes: { ee: themeList(themes.ee), eo: themeList(themes.eo) },
    difficulty: typeof data.difficulty === "number" ? data.difficulty : null,
    officialNclc: EXAM_SKILLS.flatMap((skill) => (nclc[skill] === undefined || nclc[skill] === null ? [] : [{ skill, value: num(nclc[skill]) }])),
  };
}

// --- Session analysis prepared by the AI (monthly-packs → analysis) ----------------------------

export interface GroupedTheme {
  skill: string;
  theme: string;
  count: number;
}

export interface PackAnalysis {
  /** « ai » (the AI analysed and wrote the topics) or « rules » (statistics, published topics). */
  source: string;
  summary: string;
  grouped: GroupedTheme[];
  /** "" when the exam has the official structure, else what differs. */
  structure: string;
  /** Subjects of past sessions reported online, summarised by the AI (never reproduced). */
  foundOnline: FoundSubject[];
  researchNotes: string;
  sources: { title: string; url: string }[];
}

export interface FoundSubject {
  skill: string;
  task: number;
  theme: string;
  summary: string;
  session: string;
  where: string;
}

/** Only http(s) links are shown (the pages the AI read). */
function safeUrl(value: unknown): string {
  const url = str(value);
  return /^https?:\/\//i.test(url) ? url : "";
}

export function parsePack(value: unknown): PackAnalysis {
  const data = isObject(value) ? value : {};
  const grouped = Array.isArray(data.grouped_themes) ? (data.grouped_themes as unknown[]).filter(isObject) : [];
  return {
    source: str(data.source) === "ai" ? "ai" : "rules",
    summary: str(data.summary).trim(),
    grouped: grouped.flatMap((g) => {
      const theme = str(g.theme);
      return theme ? [{ skill: str(g.skill), theme, count: num(g.count) }] : [];
    }),
    structure: str(data.structure),
    foundOnline: (Array.isArray(data.found_online) ? (data.found_online as unknown[]).filter(isObject) : []).flatMap((f) => {
      const summary = str(f.summary);
      return summary
        ? [{ skill: str(f.skill), task: num(f.task), theme: str(f.theme), summary, session: str(f.session), where: str(f.where) }]
        : [];
    }),
    researchNotes: str(data.research_notes).trim(),
    sources: (Array.isArray(data.sources) ? (data.sources as unknown[]).filter(isObject) : []).flatMap((src) => {
      const url = safeUrl(src.url);
      return url ? [{ title: str(src.title) || url, url }] : [];
    }),
  };
}

/** Found subjects grouped by skill then task: « ee-2 » → [...], in the order EE 1…3, EO 1…3. */
export function groupFound(rows: FoundSubject[]): { key: string; skill: string; task: number; items: FoundSubject[] }[] {
  const groups = new Map<string, { key: string; skill: string; task: number; items: FoundSubject[] }>();
  for (const row of rows) {
    const key = `${row.skill}-${row.task}`;
    const group = groups.get(key) ?? { key, skill: row.skill, task: row.task, items: [] };
    group.items.push(row);
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) => a.skill.localeCompare(b.skill) || a.task - b.task);
}

/** Largest count of a list (bars are drawn relative to it). */
export function maxCount(rows: { count: number }[]): number {
  return rows.reduce((m, r) => Math.max(m, r.count), 0);
}

/** The current month in the agency's time zone, « AAAA-MM ». */
export function currentMonth(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE, year: "numeric", month: "2-digit" }).formatToParts(now);
  const year = parts.find((p) => p.type === "year")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  return `${year}-${month}`;
}

/** « 2026-09 » → « 09/2026 ». */
export function monthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  return match ? `${match[2]}/${match[1]}` : month;
}

export function stringList(value: unknown): string[] {
  return Array.isArray(value) ? (value as unknown[]).map(str).filter(Boolean) : [];
}

// --- Learner progress ------------------------------------------------------------------------

export interface SkillResult {
  score: number | null;
  cefr: string;
  nclc: number | null;
}

export interface Sitting {
  attempt: string;
  kind: string;
  mock: number | null;
  at: string;
  skills: Partial<Record<ExamSkill, SkillResult>>;
}

export interface RankBlock {
  position: number;
  total: number;
  topPercent: number;
}

export type Verdict = "ready" | "almost" | "not_ready";

export interface SkillAnalysis {
  skill: ExamSkill;
  nclc: number | null;
  gap: number | null;
  trend: string;
}

export interface ProgressData {
  evolution: Sitting[];
  rank: { platform: RankBlock | null; agency: RankBlock | null };
  verdict: Verdict;
  target: number | null;
  skills: SkillAnalysis[];
  critical: { code: string; message: string }[];
  daysLeft: number | null;
  xp: number;
  streakDays: number;
  levelIndex: number | null;
}

const nullableNumber = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);

function rankBlock(value: unknown): RankBlock | null {
  if (!isObject(value)) return null;
  return { position: num(value.position), total: num(value.total), topPercent: num(value.top_percent) };
}

export function parseProgress(value: unknown): ProgressData {
  const data = isObject(value) ? value : {};
  const analysis = isObject(data.analysis) ? data.analysis : {};
  const rank = isObject(data.rank) ? data.rank : {};
  const skills = isObject(analysis.skills) ? analysis.skills : {};
  const evolution = Array.isArray(data.evolution) ? (data.evolution as unknown[]).filter(isObject) : [];
  const verdict = str(analysis.verdict);
  return {
    evolution: evolution.map((row) => {
      const results = isObject(row.skills) ? row.skills : {};
      const out: Partial<Record<ExamSkill, SkillResult>> = {};
      for (const skill of EXAM_SKILLS) {
        const r = results[skill];
        if (isObject(r)) out[skill] = { score: nullableNumber(r.score), cefr: str(r.cefr), nclc: nullableNumber(r.nclc) };
      }
      return { attempt: str(row.attempt), kind: str(row.kind), mock: nullableNumber(row.mock), at: str(row.at), skills: out };
    }),
    rank: { platform: rankBlock(rank.platform), agency: rankBlock(rank.agency) },
    verdict: verdict === "ready" || verdict === "almost" ? verdict : "not_ready",
    target: nullableNumber(analysis.target),
    skills: EXAM_SKILLS.map((skill) => {
      const s = isObject(skills[skill]) ? skills[skill] : {};
      return { skill, nclc: nullableNumber(s.nclc), gap: nullableNumber(s.gap), trend: str(s.trend) || "unknown" };
    }),
    critical: (Array.isArray(analysis.critical) ? (analysis.critical as unknown[]) : []).filter(isObject).map((c) => ({ code: str(c.code), message: str(c.message) })),
    daysLeft: nullableNumber(analysis.days_left),
    xp: num(data.xp),
    streakDays: num(data.streak_days),
    levelIndex: nullableNumber(data.level_index),
  };
}

// --- Anti-plagiarism refusal -----------------------------------------------------------------

function firstMessage(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return (value as unknown[]).map(firstMessage).find(Boolean) ?? "";
  if (isObject(value)) return firstMessage(value.message ?? value.detail ?? value.string);
  return "";
}

function hasCode(value: unknown, code: string): boolean {
  if (Array.isArray(value)) return (value as unknown[]).some((v) => hasCode(v, code));
  if (isObject(value)) return value.code === code || Object.values(value).some((v) => hasCode(v, code));
  return false;
}

/**
 * A publication refused for plagiarism: 400 with the code « too_similar » and the server's
 * message (« Contenu trop proche d'une source protégée (…) »). Without an explicit code, a 400
 * carrying only `detail` on publication is that refusal too (the only non-field error there).
 * Returns the message ("" when the server sent none), or null when it is another error.
 */
export function tooSimilarMessage(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 400 || !isObject(error.body)) return null;
  const body = error.body;
  if (hasCode(body, "too_similar")) return firstMessage(body.detail ?? body.message ?? body);
  if (typeof body.code === "string") return null; // another coded error
  const keys = Object.keys(body);
  if (keys.length === 1 && keys[0] === "detail") return firstMessage(body.detail);
  return null;
}
